import {
  Box,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "pages/phNewPendingTasks/style.module.scss";
import React, { useEffect, useState } from "react";
import PaginationComponent from "shared/components/pagination";
import RangeDatePicker from "shared/components/rangeDatePicker";
import useWindowSize from "shared/customHooks/windowSize";
import {
  feature_book_headers,
  headers_enums,
  reported_book_headers,
  sub_requests_headers,
  support_ticket_headers,
  tab_enums_service,
} from "./constants";
import FeatureBooks from "./featureBook";
import ReportedBooks from "./reportedBook";
import SubscriptionRequest from "./subscriptionRequests";
import SupportChat from "./supportChat";
import { useNavigate, useParams } from "react-router-dom";
import { routeConstant } from "shared/routes/routesConstant";
import { pendingTasksList } from "shared/services/projectHeadService";
import { roles } from "shared/utils/constants";
import moment from "moment";
import useDebounce from "shared/customHooks/useDebounce";

function AdminPendingTasks() {
  const params = useParams();
  const navigate = useNavigate();
  const screenWidth = useWindowSize();
  const [activeTab, setActiveTab] = useState<any>(0);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [headers, setHeaders] = useState<any>(sub_requests_headers);
  const [activePage, setActivePage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [allTasks, setAllTasks] = useState<any>([]);
  const [totalPage, setTotalPage] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(true);

  const tabs = [
    {
      value: 0,
      label: "Subscription Requests",
      counter: 5,
      tabs_enum: "sub_request",
      service_id: tab_enums_service.sub_requests,
    },
    {
      value: 1,
      label: "Support Chats",
      counter: 8,
      tabs_enum: "support_chat",
      service_id: tab_enums_service.support_tickets,
    },
    {
      value: 2,
      label: "Feature Books",
      counter: 6,
      tabs_enum: "feature_requests",
      service_id: tab_enums_service.feature_book,
    },
    {
      value: 3,
      label: "Reported Books",
      counter: 24,
      tabs_enum: "report_request",
      service_id: tab_enums_service.reported_book,
    },
  ];

  const handleChange = (event: any, newValue: number) => {
    setActivePage(1);
    setActiveTab(newValue);

    let filteredTab: any = tabs.findIndex((item) => item?.value === newValue);

    if (filteredTab > -1) {
      navigate(
        routeConstant.pendingTasks.path
          .replace(":keyword", "manager")
          .replace(":task", tabs[filteredTab]?.tabs_enum)
      );
    }

    if (newValue === headers_enums.sub_requests_headers) {
      setHeaders(sub_requests_headers);
    } else if (newValue === headers_enums.support_ticket_headers) {
      setHeaders(support_ticket_headers);
    } else if (newValue === headers_enums.feature_book_headers) {
      setHeaders(feature_book_headers);
    } else if (newValue === headers_enums.reported_book_headers) {
      setHeaders(reported_book_headers);
    }
  };

  useEffect(() => {
    let filteredTab: any = tabs.findIndex(
      (item) => item?.tabs_enum === params?.task
    );

    if (filteredTab > -1) {
      handleChange("", tabs[filteredTab]?.value);
    }

    // eslint-disable-next-line
  }, [params?.task]);

  const getAllTasks = () => {
    setLoader(true);
    pendingTasksList(
      searchText,
      roles.productManager,
      activePage,
      tabs?.find((item) => activeTab === item?.value)?.service_id,
      {
        from: moment(startDate).format("YYYY-MM-DD"),
        to: moment(endDate).format("YYYY-MM-DD"),
      }
    )
      .then(({ data: { data } }) => {
        if (headers_enums.sub_requests_headers === activeTab) {
          setAllTasks(data?.pending_manual_sub?.data);
          setTotalPage(data?.pending_manual_sub?.last_page);
        }

        setLoader(false);
      })
      .catch((err) => {
        console.log("Data", err?.response?.data?.message);
        setLoader(false);
      });
  };

  useEffect(() => {
    if (startDate && endDate) {
      getAllTasks();
    }

    // eslint-disable-next-line
  }, [searchText, endDate, activeTab, activePage]);

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800
  );

  return (
    <div>
      <div className={styles.topLevelContainer}>
        <div className={styles.headingContainer}>
          <label className={styles.heading}>
            Pending Tasks of Product Manager
          </label>
          <label className={styles.subHeading}>
            Table with pending task tabs, list and actions to perform.
          </label>
        </div>
        <div className={styles.rangePicker}>
          <RangeDatePicker
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={(date: Date | null) => {
              setEndDate(date);
              setActivePage(1);
            }}
            doubleLabel={true}
          />
        </div>
      </div>

      <div className={styles.mainContainer}>
        <Box className={styles.boxContainer}>
          <Tabs
            value={activeTab}
            onChange={handleChange}
            className={styles.tab}
            TabIndicatorProps={{
              style: {
                backgroundColor: "black",
              },
            }}
          >
            {tabs?.map((item: any, index: any) => {
              return (
                <Tab
                  label={
                    <div
                      className={classNames(
                        styles.tabs,
                        activeTab === item?.value && styles.active
                      )}
                    >
                      <label className={styles.tabLabel}> {item?.label}</label>
                      <label className={styles.tabCounter}>
                        {item?.counter}
                      </label>
                    </div>
                  }
                />
              );
            })}
          </Tabs>
        </Box>
        <Box className={styles.toptableContainer}>
          <div className={styles.headingContainer}>
            {activeTab === headers_enums.sub_requests_headers ? (
              <>
                <label className={styles.tableHeading}>
                  Subscription Request
                </label>
              </>
            ) : activeTab === headers_enums.support_ticket_headers ? (
              <>
                <label className={styles.tableHeading}>Support Chat</label>
              </>
            ) : activeTab === headers_enums.feature_book_headers ? (
              <>
                <label className={styles.tableHeading}>
                  Feature Book Requests
                </label>
              </>
            ) : activeTab === headers_enums.reported_book_headers ? (
              <>
                <label className={styles.tableHeading}>
                  Reported Book Requests
                </label>
              </>
            ) : (
              " "
            )}
          </div>
          <div className={styles.gridContainer}>
            <Icons.SearchIcon className={styles.searchIcon} />
            <input
              placeholder="Search"
              className={styles.input}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Box>

        <TableContainer
          component={Paper}
          className={styles.tableContainer}
          sx={screenWidth > 990 ? { width: `${screenWidth - 300}px` } : {}}
          id="table-view-dashboard"
        >
          <Table aria-label="simple table">
            <TableHead className={styles.tableHead}>
              <TableRow>
                {headers?.map((item: any) => {
                  return (
                    <>
                      <TableCell>{item}</TableCell>
                    </>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody className={styles.tableBody}>
              {activeTab === headers_enums?.sub_requests_headers ? (
                <SubscriptionRequest allTasks={allTasks} loader={loader} />
              ) : activeTab === headers_enums?.support_ticket_headers ? (
                <SupportChat />
              ) : activeTab === headers_enums?.feature_book_headers ? (
                <FeatureBooks />
              ) : (
                <ReportedBooks />
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* {
          <div className={classNames(styles.noDataCard, styles.noData)}>
            <Icons.NoDataIcon />
            <h5>No Data Found</h5>
          </div>
        } */}

        <PaginationComponent
          totalPages={totalPage}
          activePage={activePage}
          setActivePage={setActivePage}
        />
      </div>
    </div>
  );
}

export default AdminPendingTasks;
